import {
  ArrowClockwise,
  ArrowCounterClockwise,
  DotsThreeVertical,
  Headphones,
  Lock,
  Pause,
  Play,
  Trash,
  WarningCircle,
} from '@phosphor-icons/react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import Progress from '@/components/ui/progress';
import Spinner from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import {
  isAudioOverviewTerminal,
  type AudioOverviewSummary,
} from '@/types/AudioOverview';
import {
  audioOverviewFailureCopy,
  audioOverviewStageLabel,
  describeAudioOverviewFormat,
  formatAudioOverviewDuration,
} from '../audioOverviewCopy';
import {
  AUDIO_OVERVIEW_FALLBACK_TITLE,
  audioOverviewStartAt,
  isAudioOverviewCompleted,
} from '../audioOverviewPlayback';

export interface AudioOverviewRowProps {
  overview: AudioOverviewSummary;
  /** This episode is loaded in the player. */
  isCurrent?: boolean;
  /** …and playing (or about to). */
  isPlaying?: boolean;
  /** …and played to the end. */
  isEnded?: boolean;
  onPlay: (overview: AudioOverviewSummary) => void;
  onPause?: () => void;
  onRetry: (overview: AudioOverviewSummary) => void;
  onDelete: (overview: AudioOverviewSummary) => void;
  onUnlock: (overview: AudioOverviewSummary) => void;
}

export const audioOverviewRowTitle = (
  overview: AudioOverviewSummary
): string => {
  // An article's shared episode goes by the article it reads (ORA-905).
  if (overview.source === 'article' && overview.articleTitle) {
    return overview.articleTitle;
  }
  if (overview.title) return overview.title;
  return isAudioOverviewTerminal(overview.status)
    ? AUDIO_OVERVIEW_FALLBACK_TITLE
    : 'Generating…';
};

const LEADING =
  'flex size-10 shrink-0 items-center justify-center rounded-full bg-content-audio/15 text-content-audio';

/** One Audio Overview in the lecture panel or the library (ORA-735). */
export const AudioOverviewRow = ({
  overview,
  isCurrent = false,
  isPlaying = false,
  isEnded = false,
  onPlay,
  onPause,
  onRetry,
  onDelete,
  onUnlock,
}: AudioOverviewRowProps) => {
  const title = audioOverviewRowTitle(overview);
  const isGenerating = !isAudioOverviewTerminal(overview.status);
  const isFailed = overview.status === 'FAILED';
  // Only when the server says it can be retried (older servers: any FAILED).
  const canRetry = isFailed && !overview.locked && overview.retryable;
  const isReady = overview.status === 'READY';
  // Shared episodes (an article's, or one Ora made for a lecture) are nobody's to delete.
  const canDelete = overview.source !== 'article' && !overview.shared;
  const duration = formatAudioOverviewDuration(overview.durationSec);

  const meta = [
    describeAudioOverviewFormat(overview),
    isReady ? duration : '',
    isReady && overview.completedAt ? 'Finished' : '',
  ]
    .filter(Boolean)
    .join(' · ');

  const renderLeading = () => {
    if (overview.locked) {
      return (
        <div aria-hidden="true" className={LEADING}>
          <Lock className="size-5" />
        </div>
      );
    }
    if (isGenerating) {
      return (
        <div aria-hidden="true" className={LEADING}>
          <Spinner size={20} />
        </div>
      );
    }
    if (isFailed) {
      return (
        <div
          aria-hidden="true"
          className="flex size-10 shrink-0 items-center justify-center rounded-full bg-surface-muted text-status-danger"
        >
          <WarningCircle className="size-5" />
        </div>
      );
    }
    return (
      <div aria-hidden="true" className={LEADING}>
        <Headphones weight="fill" className="size-5" />
      </div>
    );
  };

  const renderPrimary = () => {
    if (overview.locked && (isReady || isGenerating)) {
      return (
        <Button
          size="sm"
          variant="outline"
          aria-label={`Unlock ${title}`}
          onClick={() => onUnlock(overview)}
        >
          <Lock aria-hidden="true" />
          Unlock
        </Button>
      );
    }
    if (isReady) {
      if (isCurrent && isPlaying) {
        return (
          <Button
            size="sm"
            variant="outline"
            aria-label={`Pause ${title}`}
            onClick={onPause}
          >
            <Pause weight="fill" aria-hidden="true" />
            Pause
          </Button>
        );
      }

      // Finished (or just ended in the player): start over, not resume.
      const isReplay = isCurrent ? isEnded : isAudioOverviewCompleted(overview);

      const label = isReplay
        ? 'Replay'
        : isCurrent || audioOverviewStartAt(overview) > 0
          ? 'Resume'
          : 'Play';

      return (
        <Button
          size="sm"
          aria-label={`${label} ${title}`}
          onClick={() => onPlay(overview)}
        >
          {isReplay ? (
            <ArrowCounterClockwise aria-hidden="true" />
          ) : (
            <Play weight="fill" aria-hidden="true" />
          )}
          {label}
        </Button>
      );
    }
    if (canRetry) {
      return (
        <Button
          size="sm"
          variant="outline"
          aria-label={`Retry ${title}`}
          onClick={() => onRetry(overview)}
        >
          <ArrowClockwise aria-hidden="true" />
          Retry
        </Button>
      );
    }
    return null;
  };

  return (
    <li
      className={cn(
        'flex items-start gap-3 rounded-lg border p-3',
        isCurrent ? 'border-content-audio' : 'border-border-subtle'
      )}
      data-testid="audio-overview-row"
      data-status={overview.status}
    >
      {renderLeading()}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p
          className="!m-0 line-clamp-2 font-heading text-sm font-medium break-words text-fg-primary"
          title={title}
        >
          {title}
        </p>
        <p className="!m-0 text-xs text-fg-secondary">{meta}</p>
        {isGenerating ? (
          <div className="flex flex-col gap-1.5 pt-1">
            <Badge variant="secondary">
              {audioOverviewStageLabel(overview.status)} · {overview.progress}%
            </Badge>
            <Progress
              value={overview.progress}
              aria-label={`${title}: ${audioOverviewStageLabel(overview.status)}`}
              indicatorStyle={{ backgroundColor: 'var(--color-ct-audio)' }}
            />
          </div>
        ) : null}
        {isFailed ? (
          <p className="!m-0 text-xs text-status-danger">
            {audioOverviewFailureCopy(
              overview.errorCode,
              overview.lectures.length
            )}
          </p>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-1 self-center">
        {renderPrimary()}
        {canRetry || canDelete ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label={`More actions for ${title}`}
              >
                <DotsThreeVertical weight="bold" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canRetry ? (
                <DropdownMenuItem onSelect={() => onRetry(overview)}>
                  <ArrowClockwise aria-hidden="true" />
                  Retry
                </DropdownMenuItem>
              ) : null}
              {canDelete ? (
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={() => onDelete(overview)}
                >
                  <Trash aria-hidden="true" />
                  Delete
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    </li>
  );
};

export default AudioOverviewRow;
